import React, { useEffect } from 'react';
import { create } from 'zustand';
import axios from 'axios';
import API_URL from '../config';

const useAuthStore = create((set, get) => ({
  user: null,
  loading: true,
  isAuthenticated: false,

  initAuth: async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Set initial state from storage for fast load
        set({ user: JSON.parse(storedUser), isAuthenticated: true, loading: true });

        // Fetch latest user data from server to sync cross-device changes (like profile photo)
        const response = await axios.get(`${API_URL}/api/v2/auth/me`);
        const latestUser = response.data.usuario || response.data.data;
        
        if (latestUser) {
            const normalizedUser = {
              ...latestUser,
              id_empresa: latestUser.id_empresa || latestUser.empresaId,
              isSuperAdmin: latestUser.esSuperAdmin || latestUser.isSuperAdmin
            };
            localStorage.setItem('user', JSON.stringify(normalizedUser));
            set({ user: normalizedUser, isAuthenticated: true, loading: false });
        } else {
            set({ loading: false });
        }
      } catch (error) {
        console.error('Sesión inválida o expirada:', error);
        get().logout();
        set({ loading: false });
      }
    } else {
      set({ loading: false });
    }
  },

  login: async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/v2/auth/login`, {
        usuario: username,
        password
      });

      const { usuario, token } = response.data.data;

      const normalizedUser = {
        ...usuario,
        id_empresa: usuario.id_empresa || usuario.empresaId,
        isSuperAdmin: usuario.esSuperAdmin || usuario.isSuperAdmin
      };

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      localStorage.setItem('id_usuario', normalizedUser.id_usuario || normalizedUser.id);
      localStorage.setItem('nombre', normalizedUser.nombre);
      localStorage.setItem('rol_usuario', normalizedUser.rol);

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      set({ user: normalizedUser, isAuthenticated: true });

      return { success: true, usuario: normalizedUser };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.error || 'Error al iniciar sesión'
      };
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    set({ user: null, isAuthenticated: false });
  },

  updateUserData: (updatedUser) => {
    const { user } = get();
    const normalizedUser = {
      ...user,
      ...updatedUser,
      isSuperAdmin: updatedUser.esSuperAdmin !== undefined ? updatedUser.esSuperAdmin : (user?.isSuperAdmin || false)
    };

    localStorage.setItem('user', JSON.stringify(normalizedUser));
    set({ user: normalizedUser });
  },

  hasRole: (roleName) => {
    const { user } = get();
    if (!user || !user.roles) return false;
    return user.roles.includes(roleName) || user.isSuperAdmin;
  },

  isSuperAdmin: () => {
    const { user } = get();
    return user?.isSuperAdmin || false;
  }
}));

export const useAuth = () => useAuthStore();

export const AuthProvider = ({ children }) => {
  const loading = useAuthStore(state => state.loading);
  const initAuth = useAuthStore(state => state.initAuth);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  if (loading) return null;

  return <>{children}</>;
};

export default AuthProvider;
